// Shared brand tokens + primitives reused across Concept A / B / C previews.
// Each concept file composes its own visual language on top of these.

import React from 'react';
import { Link } from 'react-router-dom';

export const PAPER       = '#FBFAF6';
export const NAVY        = '#0E1C2E';
export const NAVY_MUTED  = 'rgba(14, 28, 46, 0.72)';
export const NAVY_SOFT   = 'rgba(14, 28, 46, 0.55)';
export const TEAL        = '#2FB3A0';
export const TEAL_SOFT   = 'rgba(47, 179, 160, 0.10)';
export const HAIRLINE    = 'rgba(14, 28, 46, 0.10)';
export const SAGE_BG     = '#F4F0E6';
export const CORAL       = '#E07A5F';
export const CORAL_SOFT  = 'rgba(224, 122, 95, 0.10)';

export const ArrowRight = ({ size = 16, stroke = 1.7 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
export const CheckTick = ({ size = 14, color = TEAL }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M4 12l5 5L20 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
export const XMark = ({ size = 14, color = CORAL }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M6 6l12 12M18 6L6 18" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// ─── Shared top nav ──────────────────────────────────────
export const ConceptNav = ({ onStart, onLogin, user, activeConcept }) => (
  <nav
    className="w-full sticky top-0 z-40 safe-top"
    style={{ backgroundColor: PAPER, borderBottom: `1px solid ${HAIRLINE}` }}
    data-testid="concept-nav"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between gap-4">
      <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="concept-nav-logo">
        <img src="/kindra-logo.png" alt="Kindra" className="h-9 md:h-10 w-auto" />
      </Link>
      <div className="hidden md:flex items-center gap-2 text-[11px] font-sans font-medium" style={{ letterSpacing: '0.16em', textTransform: 'uppercase', color: NAVY_SOFT }}>
        <span>Preview · Concepts</span>
        <span className="opacity-30">·</span>
        <Link to="/concept-a" className={`px-2.5 py-1 rounded-full transition-colors ${activeConcept === 'A' ? 'bg-[#0E1C2E] text-[#FBFAF6]' : 'hover:text-[#0E1C2E]'}`}>A</Link>
        <Link to="/concept-b" className={`px-2.5 py-1 rounded-full transition-colors ${activeConcept === 'B' ? 'bg-[#0E1C2E] text-[#FBFAF6]' : 'hover:text-[#0E1C2E]'}`}>B</Link>
        <Link to="/concept-c" className={`px-2.5 py-1 rounded-full transition-colors ${activeConcept === 'C' ? 'bg-[#0E1C2E] text-[#FBFAF6]' : 'hover:text-[#0E1C2E]'}`}>C</Link>
        <Link to="/concept-d" className={`px-2.5 py-1 rounded-full transition-colors ${activeConcept === 'D' ? 'bg-[#0E1C2E] text-[#FBFAF6]' : 'hover:text-[#0E1C2E]'}`}>D</Link>
      </div>
      <div className="flex items-center gap-1 md:gap-4">
        {user ? (
          <button onClick={() => (window.location.href = '/home')} className="text-[13px] md:text-[14px] font-medium px-3 md:px-4 py-2 rounded-full transition-colors" style={{ color: NAVY }} data-testid="concept-nav-continue">
            Continue to Kindra
          </button>
        ) : (
          <>
            <button onClick={onLogin} className="text-[13px] md:text-[14px] font-medium px-3 md:px-4 py-2 transition-opacity hover:opacity-70" style={{ color: NAVY }} data-testid="concept-nav-signin">
              Sign in
            </button>
            <button onClick={onStart} className="inline-flex items-center gap-1.5 rounded-full text-[13px] md:text-[14px] font-medium px-4 md:px-5 py-2 transition-colors" style={{ backgroundColor: NAVY, color: PAPER }} data-testid="concept-nav-cta">
              Try free
            </button>
          </>
        )}
      </div>
    </div>
  </nav>
);

// ─── Shared footer ───────────────────────────────────────
export const ConceptFooter = () => (
  <footer className="relative" style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }} data-testid="concept-footer">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-14">
      <div className="flex flex-col lg:flex-row lg:items-start gap-10 lg:gap-20 lg:justify-between">
        <div className="flex-shrink-0 max-w-[320px]">
          <div className="flex items-center gap-3 mb-4">
            <img src="/kindra-logo.png" alt="Kindra" className="h-7 w-auto opacity-85" />
            <span className="font-sans text-[12px]" style={{ color: NAVY_SOFT, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Kindra Health</span>
          </div>
          <p className="font-sans text-[13px] leading-[1.65]" style={{ color: NAVY_SOFT }}>
            Care between appointments. Based in Brisbane, Australia.
          </p>
          <p className="font-sans text-[12px] mt-4" style={{ color: NAVY_SOFT }}>© 2026 Kindra AI Pty Ltd</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 sm:gap-10 lg:gap-16">
          <div>
            <p className="font-sans text-[11px] uppercase font-medium mb-3" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>Product</p>
            <ul className="space-y-2.5 font-sans text-[13.5px]" style={{ color: NAVY_MUTED }}>
              <li><Link to="/pricing" className="hover:text-[#0E1C2E]">Pricing</Link></li>
              <li><Link to="/faq" className="hover:text-[#0E1C2E]">FAQ</Link></li>
              <li><a href="/sample-brief" target="_blank" rel="noreferrer" className="hover:text-[#0E1C2E]">Sample brief</a></li>
            </ul>
          </div>
          <div>
            <p className="font-sans text-[11px] uppercase font-medium mb-3" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>Trust</p>
            <ul className="space-y-2.5 font-sans text-[13.5px]" style={{ color: NAVY_MUTED }}>
              <li><Link to="/privacy" className="hover:text-[#0E1C2E]">Privacy</Link></li>
              <li><Link to="/security" className="hover:text-[#0E1C2E]">Security</Link></li>
              <li><Link to="/terms" className="hover:text-[#0E1C2E]">Terms</Link></li>
              <li><Link to="/medical-disclaimer" className="hover:text-[#0E1C2E]">Medical</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-sans text-[11px] uppercase font-medium mb-3" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>Support</p>
            <ul className="space-y-2.5 font-sans text-[13.5px]" style={{ color: NAVY_MUTED }}>
              <li><Link to="/contact" className="hover:text-[#0E1C2E]">Contact</Link></li>
              <li><a href="mailto:hello@kindraai.dev" className="hover:text-[#0E1C2E]">hello@kindraai.dev</a></li>
            </ul>
          </div>
        </div>
      </div>
      <div className="mt-10 md:mt-14 pt-6 md:pt-8 font-sans text-[12px] md:text-[12.5px] leading-[1.7] max-w-[760px]" style={{ color: NAVY_SOFT, borderTop: `1px solid ${HAIRLINE}` }}>
        Kindra is a health companion — it explains, organises and remembers. It doesn't diagnose, prescribe or replace your clinician. In an emergency, call <strong>000</strong>.
      </div>
    </div>
  </footer>
);
